import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Eye, Copy, ExternalLink, LogOut, MousePointerClick, TrendingUp,
  CalendarCheck, Trophy, Loader2, Sparkles, Euro, Percent,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { logger } from '../lib/logger';
import i18n from '../i18n';
// ★ 2026-08-30 (Fase 58.N) — Training gamificato affiliato + compensi
import { AffiliateTrainingCard, TRAINING_REFRESH_EVENT } from '../components/AffiliateTrainingCard';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const STAGE_BADGE = {
  modulo_in_progress:  { label: 'Form 1 fatto',  cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  modulo2_completed:   { label: 'Qualificato',   cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  call_booked:         { label: 'Call prenotata', cls: 'bg-violet-50 text-violet-700 border-violet-200' },
  call_done:           { label: 'Call fatta',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  closed_won:          { label: 'Cliente',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  closed_lost:         { label: 'Perso',          cls: 'bg-zinc-100 text-zinc-500 border-zinc-200' },
};

/**
 * AffiliateDashboardPage — read-only dashboard for the affiliate.
 *
 * Visibility rules (server-enforced, mirrored in UI):
 *   • Affiliate sees ONLY leads where custom_fields.affiliate_id === his id.
 *   • All data is read-only — no edit, no delete, no notes.
 *   • Personal data of the lead IS shown (per agreement: "sees all his leads").
 */
export default function AffiliateDashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  // ★ 2026-07-21: se qualche endpoint fallisce non blocchiamo l'intero render
  const [loadError, setLoadError] = useState('');
  // ★ 2026-08-30 (Fase 58.N) — filtro periodo per fatturato/commissioni
  const [periodDays, setPeriodDays] = useState('all');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    // ★ 2026-07-21 (fix schermata bianca): usiamo Promise.allSettled invece
    // di Promise.all così se `stats` o `leads` falliscono la pagina si
    // renderizza comunque con `me` (basic info).
    try {
      const [meR, statsR, leadsR] = await Promise.allSettled([
        api.get('/affiliate/me'),
        api.get('/affiliate/stats'),
        api.get('/affiliate/leads'),
      ]);
      if (meR.status === 'fulfilled') {
        setMe(meR.value.data?.affiliate);
      } else {
        const err = meR.reason;
        logger.error('affiliate /me failed', err);
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 403) {
          // Utente non è affiliato → forziamo logout redirect
          toast.error(detail || 'Accesso non autorizzato');
          nav('/login', { replace: true });
          return;
        }
        setLoadError(detail || 'Errore caricamento profilo affiliato');
      }
      if (statsR.status === 'fulfilled') {
        setStats(statsR.value.data?.stats);
      } else {
        logger.warn('affiliate /stats failed (non-blocking)', statsR.reason);
      }
      if (leadsR.status === 'fulfilled') {
        setLeads(leadsR.value.data?.leads || []);
      } else {
        logger.warn('affiliate /leads failed (non-blocking)', leadsR.reason);
      }
    } catch (err) {
      logger.error('affiliate dashboard fatal error', err);
      setLoadError('Errore imprevisto nel caricamento della dashboard');
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ★ Fase 58.N — refetch stats al cambio periodo (fatturato/commissioni)
  useEffect(() => {
    (async () => {
      try {
        const params = periodDays !== 'all' ? { days: Number(periodDays) } : {};
        const { data } = await api.get('/affiliate/stats', { params });
        setStats(data?.stats);
      } catch (err) {
        logger.warn('affiliate /stats period refetch failed', err);
      }
    })();
  }, [periodDays]);

  const copyLink = useCallback(async () => {
    if (!me?.link) return;
    try {
      await navigator.clipboard.writeText(me.link);
      toast.success('Link copiato');
      // ★ Fase 58.N — missione training "copia il tuo link referral"
      try {
        const { data } = await api.post('/training/ui', { mission_key: 'copy_referral_link' });
        (data?.newly_completed || []).forEach((m) => {
          toast.success(i18n.t('trainingPage.missionCompleted', { defaultValue: 'Missione completata: {{title}}', title: m.title }), { description: i18n.t('trainingPage.pointsGained', { defaultValue: '+{{points}} punti', points: m.points }) });
        });
        if (data?.finished) toast.success(i18n.t('trainingPage.trainingFinishedShort', 'Training completato! Certificato HCF ottenuto'));
        window.dispatchEvent(new CustomEvent(TRAINING_REFRESH_EVENT));
      } catch { /* non-blocking: la copia resta valida */ }
    } catch (err) {
      logger.debug('clipboard copy failed', err);
      toast.error('Copia non riuscita');
    }
  }, [me?.link]);

  const handleLogout = useCallback(() => {
    logout?.();
    nav('/login', { replace: true });
  }, [logout, nav]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="affiliate-dashboard-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-1,#fbfaf6)]" data-testid="affiliate-dashboard-page">
      {/* Top bar */}
      <header className="bg-[var(--gp-forest,#0F2A1B)] text-white">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="font-serif text-lg md:text-xl">Green Paradise · Affiliati</div>
            <Badge className="hidden md:inline-flex bg-white/10 text-white/90 hover:bg-white/15 border border-white/15">
              <Eye className="w-3 h-3 mr-1" /> modalità spettatore
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden md:block text-right">
              <div className="text-xs text-white/60">Loggato come</div>
              <div className="text-sm font-medium">{me?.name || user?.email}</div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="affiliate-logout">
              <LogOut className="w-4 h-4 md:mr-1.5" /> <span className="hidden md:inline">Esci</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-6">
        {/* ★ 2026-07-21: banner errore non-blocking se una API fallisce */}
        {loadError && (
          <div className="rounded-md bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900" data-testid="affiliate-load-error">
            ⚠️ {loadError} — {' '}
            <button
              type="button"
              className="underline font-medium"
              onClick={fetchAll}
              data-testid="affiliate-load-retry"
            >
              Riprova
            </button>
          </div>
        )}

        {/* Welcome + link */}
        <Card data-testid="affiliate-link-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="w-4 h-4 text-[var(--gp-gold,#C9A34A)]" />
              Ciao {me?.name?.split(' ')[0] || 'affiliato'}
            </CardTitle>
            <CardDescription>
              Condividi il tuo link personalizzato. Ogni nuovo lead che si registra dal link compare qui sotto.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row md:items-center gap-2 bg-muted/40 border border-[var(--gp-gold,#C9A34A)]/30 rounded-lg px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Il tuo link affiliato</div>
                <div className="font-mono text-sm text-[hsl(var(--primary))] truncate" data-testid="affiliate-link-value">{me?.link}</div>
              </div>
              <div className="flex gap-2">
                <Button onClick={copyLink} variant="outline" data-testid="affiliate-link-copy">
                  <Copy className="w-4 h-4 mr-1.5" /> Copia
                </Button>
                <a href={me?.link} target="_blank" rel="noopener noreferrer">
                  <Button variant="ghost">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Apri
                  </Button>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ★ Fase 58.N — Training gamificato affiliato */}
        <AffiliateTrainingCard />

        {/* KPI */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiTile icon={<MousePointerClick className="w-4 h-4" />} label="Click ricevuti" value={stats?.clicks ?? 0} testid="aff-kpi-clicks" />
          <KpiTile icon={<TrendingUp className="w-4 h-4" />} label="Lead" value={stats?.total ?? 0} testid="aff-kpi-leads" />
          <KpiTile icon={<TrendingUp className="w-4 h-4" />} label="% Conversione" value={`${stats?.conversion_rate ?? 0}%`} testid="aff-kpi-conversion" />
          <KpiTile icon={<CalendarCheck className="w-4 h-4" />} label="Call prenotate" value={stats?.booked ?? 0} testid="aff-kpi-booked" />
          <KpiTile icon={<Trophy className="w-4 h-4" />} label="Clienti" value={stats?.won ?? 0} highlight testid="aff-kpi-won" />
        </div>

        {/* ★ Fase 58.N — Fatturato portato & commissioni spettanti */}
        <Card data-testid="affiliate-earnings-card" className="border-[var(--gp-gold,#C9A34A)]/40">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Euro className="w-4 h-4 text-[var(--gp-gold,#C9A34A)]" /> I tuoi compensi
                </CardTitle>
                <CardDescription>
                  Il fatturato dei clienti che hai portato e le commissioni che ti spettano.
                </CardDescription>
              </div>
              <Select value={periodDays} onValueChange={setPeriodDays}>
                <SelectTrigger className="w-[170px] h-9" data-testid="affiliate-period-filter">
                  <SelectValue placeholder="Periodo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Da sempre</SelectItem>
                  <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                  <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  <SelectItem value="365">Ultimo anno</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border border-border bg-muted/30 p-4" data-testid="affiliate-revenue-tile">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                  <TrendingUp className="w-4 h-4" /> Fatturato portato
                </div>
                <div className="text-xl md:text-2xl font-serif text-[hsl(var(--primary))]">
                  {(stats?.revenue_eur ?? 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </div>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-4" data-testid="affiliate-commission-pct-tile">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                  <Percent className="w-4 h-4" /> La tua %
                </div>
                <div className="text-xl md:text-2xl font-serif text-[hsl(var(--primary))]">
                  {stats?.commission_pct ? `${stats.commission_pct}%` : '—'}
                </div>
                {!stats?.commission_pct && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">Da definire con il team HCF</div>
                )}
              </div>
              <div className="rounded-lg border border-[var(--gp-gold,#C9A34A)]/40 bg-[var(--gp-gold,#C9A34A)]/10 p-4" data-testid="affiliate-commission-tile">
                <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                  <Euro className="w-4 h-4" /> Commissioni spettanti
                </div>
                <div className="text-xl md:text-2xl font-serif text-[hsl(var(--primary))]">
                  {(stats?.commission_eur ?? 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leads table */}
        <Card data-testid="affiliate-leads-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">I tuoi lead</CardTitle>
            <CardDescription>
              {leads.length === 0 ? 'Nessun lead ancora. Inizia a condividere il tuo link.' : `${leads.length} lead totali (più recenti in alto).`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {leads.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground" data-testid="affiliate-leads-empty">
                <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p>I lead appariranno qui non appena qualcuno si registra dal tuo link.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead className="hidden md:table-cell">Contatti</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead className="hidden md:table-cell">Paese / Percorso</TableHead>
                      <TableHead className="text-right">Registrato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {leads.map((lead) => {
                      const badge = STAGE_BADGE[lead.stage] || { label: lead.stage || '—', cls: 'bg-zinc-100 text-zinc-600' };
                      const country = lead.qualifica?.countries?.[0];
                      const path = lead.qualifica?.path;
                      return (
                        <TableRow key={lead.id} data-testid={`affiliate-lead-row-${lead.id}`}>
                          <TableCell>
                            <div className="font-medium text-sm">{lead.name || '—'}</div>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                            <div>{lead.email || '—'}</div>
                            <div>{lead.phone || '—'}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={badge.cls}>{badge.label}</Badge>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground capitalize">
                            {country ? country : '—'}
                            {path ? <span className="mx-1">·</span> : null}
                            {path ? <span className="capitalize">{(path || '').replace('_', ' ')}</span> : null}
                          </TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                            {lead.created_at ? new Date(lead.created_at).toLocaleString('it-IT', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
                            }) : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}

function KpiTile({ icon, label, value, highlight, testid }) {
  return (
    <Card data-testid={testid} className={highlight ? 'bg-[var(--gp-gold,#C9A34A)]/10 border-[var(--gp-gold,#C9A34A)]/40' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
          {icon}
          {label}
        </div>
        <div className="text-xl md:text-2xl font-serif text-[hsl(var(--primary))]">{value}</div>
      </CardContent>
    </Card>
  );
}
